import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, X, CalendarCheck, Archive, ClockCounterClockwise } from "@phosphor-icons/react";

function fmt(dt) {
  return new Date(dt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const statusStyles = {
  pending: "border-[#FF3B30] text-[#FF3B30]",
  confirmed: "border-emerald-600 text-emerald-700",
  rejected: "border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-500 line-through",
};

export default function Calendar() {
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/appointments", {
      params: {
        archived: showArchived ? "true" : "false",
        show_past: showPast || showArchived ? "true" : "false",
      },
    });
    setItems(r.data);
  }, [showArchived, showPast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await api.post(`/appointments/${id}/approve`);
      toast.success("Appointment confirmed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    await api.post(`/appointments/${id}/reject`);
    toast("Rejected");
    load();
  };
  const archiveApt = async (id, currentlyArchived) => {
    await api.post(`/appointments/${id}/${currentlyArchived ? "unarchive" : "archive"}`);
    toast(currentlyArchived ? "Restored" : "Archived");
    load();
  };

  const grouped = items.reduce((acc, a) => {
    const d = new Date(a.start).toDateString();
    (acc[d] ||= []).push(a);
    return acc;
  }, {});

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-neutral-500 dark:text-neutral-500 mb-2">Calendar</div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {showArchived ? "Archived appointments" : "Appointments"}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="cal-toggle-past"
            onClick={() => setShowPast((v) => !v)}
            disabled={showArchived}
            title="Show past appointments"
            className={`text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${showPast ? "bg-neutral-100 dark:bg-neutral-800" : ""}`}
          >
            <ClockCounterClockwise size={12} /> {showPast ? "Hide past" : "Show past"}
          </button>
          <button
            data-testid="cal-toggle-archived"
            onClick={() => setShowArchived((v) => !v)}
            className="text-xs px-3 py-2 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"
          >
            <Archive size={12} /> {showArchived ? "Show active" : "Show archived"}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg p-10 text-center text-sm text-neutral-500 dark:text-neutral-500">
          {showArchived
            ? "No archived appointments."
            : "No upcoming appointments. AI-drafted appointments will appear here awaiting your approval."}
        </div>
      ) : (
        Object.entries(grouped).map(([day, list]) => (
          <div key={day} className="mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-500 mb-3">{day}</div>
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg bg-white dark:bg-neutral-900 divide-y divide-neutral-100 dark:divide-neutral-800">
              {list.map((a) => {
                const isPast = new Date(a.end) < new Date();
                return (
                  <div key={a.appointment_id} data-testid={`apt-row-${a.appointment_id}`} className={`p-4 flex items-center justify-between gap-4 ${isPast ? "opacity-70" : ""}`}>
                    <div className="flex items-center gap-4 min-w-0">
                      <CalendarCheck size={18} className="text-neutral-500 dark:text-neutral-500 shrink-0" weight="duotone" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-500">{a.customer_name} · {fmt(a.start)} → {fmt(a.end)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isPast && !showArchived && (
                        <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border border-neutral-300 dark:border-neutral-700 text-neutral-500 dark:text-neutral-500">Past</span>
                      )}
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${statusStyles[a.status] || ""}`}>{a.status}</span>
                      {a.status === "pending" && !showArchived && (
                        <>
                          <button data-testid={`apt-approve-${a.appointment_id}`} onClick={() => approve(a.appointment_id)} className="text-xs px-3 py-1.5 rounded-md bg-[#0F52BA] text-white hover:bg-[#0C4094] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Approve</button>
                          <button data-testid={`apt-reject-${a.appointment_id}`} onClick={() => reject(a.appointment_id)} className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center gap-1"><X size={14} /> Reject</button>
                        </>
                      )}
                      <button
                        data-testid={`apt-archive-${a.appointment_id}`}
                        onClick={() => archiveApt(a.appointment_id, showArchived)}
                        title={showArchived ? "Restore" : "Archive"}
                        className="text-xs p-1.5 rounded-md border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                      >
                        <Archive size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
